// Lighting control data: endpoints with their live state, actions, and API
// keys (P3). Endpoint state follows docs/modules/processor-drivers.md#state-model.
import { ApiError, call, callNoContent } from './fleetApi';

export interface InventoryItem {
  id: string;
  name: string;
}

export interface Inventory {
  presets: InventoryItem[];
  scenes: InventoryItem[];
  groups: InventoryItem[];
}

export interface ItemState {
  active?: boolean;
  state?: string;
  onstage?: boolean;
}

export interface GroupState {
  intensity?: number;
  rgb?: [number, number, number];
}

export interface StateDoc {
  presets?: Record<string, ItemState>;
  scenes?: Record<string, ItemState>;
  groups?: Record<string, GroupState>;
}

export interface ActionSpec {
  name: string;
  label?: string;
}

export interface EndpointState {
  online: boolean;
  error: string | null;
  driver: Record<string, unknown>;
  capabilities: { actions?: ActionSpec[] };
  inventory: Inventory;
  state: StateDoc;
  updated_at: string;
}

export interface Endpoint {
  id: string;
  site_id: string;
  appliance_id: string;
  name: string;
  driver: string;
  settings: Record<string, unknown>;
  has_credentials: boolean;
  created_at: string;
  /** Whether the endpoint's appliance is connected (state is last known while not). */
  appliance_online?: boolean;
  state: EndpointState | null;
}

/** A live endpoint update from /v1/ws. */
export interface EndpointUpdate {
  kind: 'snapshot' | 'changed' | 'refresh' | 'deleted';
  id: string;
  site_id: string;
  appliance_id: string;
  online?: boolean;
  error?: string;
  patch?: Record<string, unknown>;
  snapshot?: EndpointState;
}

export interface ActionRequest {
  endpoint_id: string;
  action: string;
  params: Record<string, unknown>;
}

export interface ActionOutcome {
  command_id: string;
  coalesced: boolean;
  processor_ms?: number;
  duration_ms: number;
}

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/** RFC 7396 JSON merge patch (as the server and appliance apply it). */
export function mergePatch<T>(doc: T, patch: unknown): T {
  if (!isObject(patch)) return patch as T;
  const out = new Map<string, unknown>(isObject(doc) ? Object.entries(doc) : []);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) out.delete(k);
    else out.set(k, mergePatch(out.get(k), v));
  }
  return Object.fromEntries(out) as T;
}

/** Applies a live update to an endpoint; null means "reload it". */
export function applyUpdate(e: Endpoint, u: EndpointUpdate): Endpoint | null {
  if (u.kind === 'snapshot' && u.snapshot) return { ...e, state: u.snapshot };
  if (u.kind !== 'changed' || !e.state) return null;
  const state: EndpointState = { ...e.state };
  if (u.patch) state.state = mergePatch(state.state, u.patch);
  if (u.online !== undefined) {
    state.online = u.online;
    if (u.online) state.error = null;
  }
  if (u.error !== undefined) state.error = u.error;
  return { ...e, state };
}

const hasArray =
  <K extends string>(key: K) =>
  (v: unknown): v is Record<K, unknown[]> =>
    isObject(v) && Array.isArray(v[key]);

function isEndpoint(v: unknown): v is Endpoint {
  return isObject(v) && typeof v.id === 'string' && typeof v.name === 'string';
}

const base = (apiUrl: string, tenantId: string, siteId: string) =>
  `${apiUrl}/v1/tenants/${encodeURIComponent(tenantId)}/sites/${encodeURIComponent(siteId)}`;

export async function fetchEndpoints(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  signal?: AbortSignal,
): Promise<Endpoint[]> {
  const body = await call(
    `${base(apiUrl, tenantId, siteId)}/endpoints`,
    token,
    { signal },
    hasArray('endpoints'),
    fetch,
  );
  return body.endpoints.filter(isEndpoint);
}

export async function fetchEndpoint(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  id: string,
): Promise<Endpoint> {
  return call(
    `${base(apiUrl, tenantId, siteId)}/endpoints/${encodeURIComponent(id)}`,
    token,
    {},
    isEndpoint,
    fetch,
  );
}

export interface NewEndpoint {
  appliance_id: string;
  name: string;
  driver: string;
  settings: Record<string, unknown>;
  credentials?: { username: string; password: string };
}

export async function createEndpoint(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  ep: NewEndpoint,
): Promise<Endpoint> {
  return call(
    `${base(apiUrl, tenantId, siteId)}/endpoints`,
    token,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(ep) },
    isEndpoint,
    fetch,
  );
}

export async function deleteEndpoint(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  id: string,
): Promise<void> {
  await callNoContent(
    `${base(apiUrl, tenantId, siteId)}/endpoints/${encodeURIComponent(id)}`,
    token,
    {
      method: 'DELETE',
    },
  );
}

function isOutcome(v: unknown): v is ActionOutcome {
  return isObject(v) && typeof v.command_id === 'string';
}

/** POST .../actions: the REST path (the live socket is preferred). */
export async function postAction(
  apiUrl: string,
  token: string,
  tenantId: string,
  siteId: string,
  req: ActionRequest,
): Promise<ActionOutcome> {
  return call(
    `${base(apiUrl, tenantId, siteId)}/actions`,
    token,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(req) },
    isOutcome,
    fetch,
  );
}

// ---------------------------------------------------------------- API keys

export interface ApiKey {
  key_id: string;
  name: string;
  scopes: string[];
  site_ids: string[] | null;
  expires_at: string | null;
  created_at: string;
  last_used_at: string | null;
  last_used_ip: string | null;
  owner: { user_id: string; email: string | null } | null;
  /** The full secret: only in the response that created it. */
  key?: string;
}

export interface NewApiKey {
  name: string;
  scopes: string[];
  site_ids?: string[];
  expires_at?: string;
}

function isKey(v: unknown): v is ApiKey {
  return isObject(v) && typeof v.key_id === 'string' && Array.isArray(v.scopes);
}

const keysUrl = (apiUrl: string, tenantId: string) =>
  `${apiUrl}/v1/tenants/${encodeURIComponent(tenantId)}/api-keys`;

export async function fetchApiKeys(
  apiUrl: string,
  token: string,
  tenantId: string,
  signal?: AbortSignal,
): Promise<ApiKey[]> {
  const body = await call(
    keysUrl(apiUrl, tenantId),
    token,
    { signal },
    hasArray('api_keys'),
    fetch,
  );
  return body.api_keys.filter(isKey);
}

export async function createApiKey(
  apiUrl: string,
  token: string,
  tenantId: string,
  key: NewApiKey,
): Promise<ApiKey> {
  return call(
    keysUrl(apiUrl, tenantId),
    token,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(key) },
    isKey,
    fetch,
  );
}

export async function deleteApiKey(
  apiUrl: string,
  token: string,
  tenantId: string,
  keyId: string,
): Promise<void> {
  await callNoContent(`${keysUrl(apiUrl, tenantId)}/${encodeURIComponent(keyId)}`, token, {
    method: 'DELETE',
  });
}

/** Readable message for an action or API failure. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError || err instanceof Error) return err.message;
  return String(err);
}
